
import { Routes, Route, useLocation } from "react-router-dom";
import RootLayout from "@/layouts/RootLayout";
import { Suspense, lazy } from "react";
import { adminRoutes } from "@/routes/adminRoutes";
import { guestRoutes } from "@/routes/guestRoutes";
import { hostRoutes } from "@/routes/hostRoutes";
import { ErrorBoundaryProvider } from "@/contexts/ErrorBoundaryContext";
import { UserProvider } from "@/contexts/UserContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { Loader2 } from "lucide-react";

// Global loading component for Suspense
const GlobalLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-10 w-10 animate-spin text-primary" />
  </div>
);

// Lazy load pages with logical grouping
const HomePage = lazy(() => import("@/pages/HomePage"));
const AuthPages = {
  Login: lazy(() => import("@/pages/LoginPage")),
  Signup: lazy(() => import("@/pages/SignupPage")),
};

const PropertyPages = {
  List: lazy(() => import("@/pages/PropertiesPage")),
  Detail: lazy(() => import("@/pages/PropertyPage")),
};

const ExperiencePages = {
  List: lazy(() => import("@/pages/ExperiencesPage")),
  Booking: lazy(() => import("@/pages/ExperienceBookingPage")),
};

const PaymentPages = {
  Details: lazy(() => import("@/pages/PaymentDetailsPage")),
  Process: lazy(() => import("@/pages/PaymentPage")),
  Methods: lazy(() => import("@/pages/PaymentMethodsPage")),
  Confirmation: lazy(() => import("@/pages/BookingConfirmationPage")),
  BillingEdit: lazy(() => import("@/pages/guest/GuestBillingEditPage")),
};

// About pages
const AboutPages = {
  HowItWorks: lazy(() => import("@/pages/about/HowItWorksPage")),
  Careers: lazy(() => import("@/pages/about/CareersPage")),
  Press: lazy(() => import("@/pages/about/PressPage")),
  Blog: lazy(() => import("@/pages/about/BlogPage")),
};

// Community pages
const CommunityPages = {
  Hosts: lazy(() => import("@/pages/community/HostsPage")),
  Referrals: lazy(() => import("@/pages/community/ReferralsPage")),
  Forum: lazy(() => import("@/pages/community/ForumPage")),
};

// Support pages
const SupportPages = {
  HelpCenter: lazy(() => import("@/pages/support/HelpCenterPage")),
  Safety: lazy(() => import("@/pages/support/SafetyPage")),
  CancellationOptions: lazy(() => import("@/pages/support/CancellationOptionsPage")),
};

// Legal pages
const LegalPages = {
  PrivacyPolicy: lazy(() => import("@/pages/legal/PrivacyPolicyPage")),
  TermsOfService: lazy(() => import("@/pages/legal/TermsOfServicePage")),
  TrustAndSafety: lazy(() => import("@/pages/legal/TrustAndSafetyPage")),
};

// Help pages
const HelpBookingPages = {
  HowToBook: lazy(() => import("@/pages/help/booking/HowToBookPage")),
  CancellationPolicies: lazy(() => import("@/pages/help/booking/CancellationPoliciesPage")),
  PaymentMethods: lazy(() => import("@/pages/help/booking/PaymentMethodsPage")),
};

const HelpHostPages = {
  GettingStarted: lazy(() => import("@/pages/help/host/GettingStartedPage")),
  ManageListings: lazy(() => import("@/pages/help/host/ManageListingsPage")),
};

const HelpAccountPages = {
  AccountSettings: lazy(() => import("@/pages/help/account/AccountSettingsPage")),
  ProfileVerification: lazy(() => import("@/pages/help/account/ProfileVerificationPage")),
  SecurityTips: lazy(() => import("@/pages/help/account/SecurityTipsPage")),
};

const App = () => {
  return (
    <ErrorBoundaryProvider>
      <UserProvider>
        <ToastProvider>
          <Suspense fallback={<GlobalLoadingFallback />}>
            <Routes>
              <Route element={<RootLayout />}>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<AuthPages.Login />} />
                <Route path="/signup" element={<AuthPages.Signup />} />
                <Route path="/properties" element={<PropertyPages.List />} />
                <Route path="/properties/:id" element={<PropertyPages.Detail />} />
                <Route path="/experiences" element={<ExperiencePages.List />} />
                <Route path="/experiences/:id/book" element={<ExperiencePages.Booking />} />
                <Route path="/payment/details/:bookingId" element={<PaymentPages.Details />} />
                <Route path="/payment/:bookingId" element={<PaymentPages.Process />} />
                <Route path="/payment/methods" element={<PaymentPages.Methods />} />
                <Route path="/booking-confirmation/:bookingId" element={<PaymentPages.Confirmation />} />
                <Route path="/billing/edit" element={<PaymentPages.BillingEdit />} />

                {/* About routes */}
                <Route path="/how-it-works" element={<AboutPages.HowItWorks />} />
                <Route path="/careers" element={<AboutPages.Careers />} />
                <Route path="/press" element={<AboutPages.Press />} />
                <Route path="/blog" element={<AboutPages.Blog />} />

                {/* Community routes */}
                <Route path="/hosts" element={<CommunityPages.Hosts />} />
                <Route path="/referrals" element={<CommunityPages.Referrals />} />
                <Route path="/forum" element={<CommunityPages.Forum />} />

                {/* Support routes */}
                <Route path="/help" element={<SupportPages.HelpCenter />} />
                <Route path="/safety" element={<SupportPages.Safety />} />
                <Route path="/cancellation-options" element={<SupportPages.CancellationOptions />} />

                {/* Legal routes */}
                <Route path="/privacy-policy" element={<LegalPages.PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<LegalPages.TermsOfService />} />
                <Route path="/trust-and-safety" element={<LegalPages.TrustAndSafety />} />

                {/* Help Center Routes */}
                <Route path="/help/booking/how-to-book" element={<HelpBookingPages.HowToBook />} />
                <Route path="/help/booking/cancellation-policies" element={<HelpBookingPages.CancellationPolicies />} />
                <Route path="/help/booking/payment-methods" element={<HelpBookingPages.PaymentMethods />} />
                <Route path="/help/host/getting-started" element={<HelpHostPages.GettingStarted />} />
                <Route path="/help/host/manage-listings" element={<HelpHostPages.ManageListings />} />
                <Route path="/help/account/settings" element={<HelpAccountPages.AccountSettings />} />
                <Route path="/help/account/verification" element={<HelpAccountPages.ProfileVerification />} />
                <Route path="/help/account/security" element={<HelpAccountPages.SecurityTips />} />

                {/* Role-specific routes */}
                <Route path="/admin/*" element={adminRoutes.element}>
                  {adminRoutes.children.map((route) => (
                    <Route key={route.path} path={route.path} element={
                      <Suspense fallback={<GlobalLoadingFallback />}>
                        {route.element}
                      </Suspense>
                    } />
                  ))}
                </Route>

                <Route path="/guest/*" element={guestRoutes.element}>
                  {guestRoutes.children.map((route) => (
                    <Route key={route.path} path={route.path} element={
                      <Suspense fallback={<GlobalLoadingFallback />}>
                        {route.element}
                      </Suspense>
                    } />
                  ))}
                </Route>

                <Route path="/host/*" element={hostRoutes.element}>
                  {hostRoutes.children.map((route) => (
                    <Route key={route.path} path={route.path} element={
                      <Suspense fallback={<GlobalLoadingFallback />}>
                        {route.element}
                      </Suspense>
                    } />
                  ))}
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </ToastProvider>
      </UserProvider>
    </ErrorBoundaryProvider>
  );
};

export default App;
